import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {Site} from "../../../domain/cloud-topology";
import { Observable, Subject, take } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {SiteService} from "../../../services/site/site.service";
import { map, takeUntil } from 'rxjs/operators';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { PermissionService } from '../../../services/permission.service';
import { PermissionEnum } from '../../../permission.enum';

@Component({
    selector: 'app-site-home',
    templateUrl: './site-home.component.html',
    styleUrls: ['./site-home.component.scss'],
    standalone: false
})
export class SiteHomeComponent implements OnInit, AfterViewInit, OnDestroy {
  site: Site;
  active = 1;
  devicesDirty: boolean;
  financialDirty: boolean;
  tab: string;
  hasOperationsPermission = false;

  private siteId: string;
  private subject$: Subject<void>;
  readonly FINANCIAL_NAME = "Financial";
  readonly DEVICES_NAME = "Devices";
  readonly OPERATIONS_NAME = "Operations";
  readonly dialog = inject(MatDialog);
  @ViewChild(MatTabGroup) matTabGroup: MatTabGroup;

  constructor(private route: ActivatedRoute,
              private siteService: SiteService,
              private router: Router,
              private permissionService: PermissionService) { }

  async ngOnInit(): Promise<void> {
    this.subject$ = new Subject<void>();
    this.route.parent.paramMap.subscribe(paramMap => {
      this.devicesDirty = false;
      this.financialDirty = false;
      this.siteId = undefined;
      this.hasOperationsPermission = false;

      setTimeout(() => {
        this.siteId = paramMap.get('id');
        this.siteService.getSite(this.siteId, true).pipe(takeUntil(this.subject$)).subscribe(siteData => {
          this.site = siteData;
          if (this.site?.id) {
            this.siteId = this.site.id;
            this.setHasOperations();
          }
        });
      })
    });
  }

  async ngAfterViewInit(): Promise<void> {
    this.tab = this.route.snapshot.queryParams['tab'];
    if (this.tab) {
      this.matTabGroup.selectedIndex = this.tab === this.FINANCIAL_NAME.toLowerCase() ? 0 : 1;
      this.matTabGroup.selectedIndex = this.tab === this.DEVICES_NAME.toLowerCase() ? 1 : 2;
    } else {
      // default to financial tab
      await this.changeTab(this.FINANCIAL_NAME.toLowerCase());
    }

    this.matTabGroup.selectedTabChange.subscribe(tabEvent => {
      this.changeTab(tabEvent.tab.textLabel.toLowerCase());
    })
  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  changed(event: MatTabChangeEvent): void {
    this.tab = event.tab.textLabel === this.FINANCIAL_NAME ? this.FINANCIAL_NAME.toLowerCase() : this.DEVICES_NAME.toLowerCase();
  }

  private async changeTab(tab: string): Promise<void> {
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: tab,
      },
      queryParamsHandling: 'merge',
    });
  }

  private setHasOperations() {
    this.permissionService.checkPermissionForEntity([PermissionEnum.execute], `OPERATIONS`, this.siteId)
      .subscribe((hasOperations) => this.hasOperationsPermission = hasOperations);
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean>  {
    if (this.devicesDirty || this.financialDirty) {
      const modalRef = this.dialog.open(ConfirmModalComponent, {
        data: { title: 'Warning', body: 'You have unsaved changes. Are you sure you wish to leave?' }
      });
      return modalRef.afterClosed();
    } else {
      return true;
    }
  }
}
